import { deriveHistoryCacheCap } from './history-cache.js';
import type { SDKStorage } from '../storage/types.js';
import { DurableCache } from './durable.js';

/**
 * List page-window cache — one page window per query signature + offset.
 * The same machinery the history cache uses (one LRU, identity-scoped,
 * recency-evicted, no clocks in freshness decisions); pages are stored
 * exactly as the resource returned them.
 *
 * Invalidation: `catalog.changed` for the affected scope, the
 * owning mutations at the mutation point, and reconnect (evict all — heal is
 * by refetch, never replay).
 */
export class PageWindowCache<P> {
  private entries = new Map<string, { page: P; lastReadAt: number }>();
  private lastIdentity: string | null = null;
  private durable: DurableCache<P>;

  constructor(
    private capEntries: number = deriveHistoryCacheCap(),
    durableKeyPrefix = 'list:',
    storage: SDKStorage | null = null,
  ) {
    this.durable = new DurableCache<P>(storage, durableKeyPrefix, (key, value) => {
      // Hydration re-inserts WITHOUT re-writing the mirror.
      this.entries.set(key, { page: value, lastReadAt: 0 });
    });
  }

  /** Entries never cross accounts (sdk ADR-a9d91). */
  prepare(identity: string): void {
    if (this.lastIdentity === identity) return;
    if (this.lastIdentity !== null) {
      this.entries.clear();
      this.durable.evictIdentityDurable(this.lastIdentity);
    }
    this.lastIdentity = identity;
    this.durable.bind(identity);
  }

  /**
   * Synchronous peek at the bound identity's entry — the TanStack
   * `initialData` seed: the return leg paints from the cache on the first
   * render, before any async identity resolution. Memory holds only the
   * bound identity's entries; absent, or when `identity` is not the bound
   * identity → null (sdk ADR-a9d91).
   */
  peekLast(identity: string, key: string): P | null {
    if (this.lastIdentity !== identity) return null;
    const entry = this.entries.get(key);
    return entry ? entry.page : null;
  }

  lookup(identity: string, key: string): P | null {
    this.prepare(identity);
    const entry = this.entries.get(key);
    if (!entry) return null;
    this.entries.delete(key);
    entry.lastReadAt = Date.now();
    this.entries.set(key, entry);
    return entry.page;
  }

  store(identity: string, key: string, page: P): void {
    this.prepare(identity);
    this.entries.delete(key);
    this.entries.set(key, { page, lastReadAt: Date.now() });
    this.durable.writeThrough(key, page);
    while (this.entries.size > this.capEntries) {
      const lru = this.entries.keys().next();
      if (lru.done) break;
      this.entries.delete(lru.value);
      this.durable.remove(lru.value);
    }
  }

  /** Evict every entry whose key starts with the prefix (a scope's windows). */
  evictPrefix(prefix: string): void {
    this.durable.evictDurablePrefix(prefix);
    for (const key of [...this.entries.keys()]) {
      if (key.startsWith(prefix)) this.entries.delete(key);
    }
  }

  evictAll(): void {
    this.entries.clear();
    this.durable.evictAllDurable();
  }
}
